/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
  IconButton,
  Popover,
  ListItem,
  Button,
  Box,
  ListItemButton,
  Typography,
  Grow,  
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import debounce, { DebouncedFunc } from "lodash.debounce";
import { useTheme } from "@mui/material/styles";
import { UserDto } from "../../types/User";
import useApi from "../../hooks/useApi";
import { getActiveUsers } from "../../services/shareResourceService";
import { ACTIVITIES, DROPDOWN_CONFIG } from "../../data/constants";
import "./UsersDropdownList.scss";

interface User {
  userId: number;
  userName: string;
  isChecked: boolean;
}

interface Props {
  userId: number | null;
  controlHeight: number;
  onUserChange: (selectedUserIds: number[]) => void;
  debounceChange: boolean;
  mtValue?: number | null;
}

const LIST_MAX_HEIGHT = 250;
const LIST_MIN_HEIGHT = 120;
//* Search box + Select All row rendered above the virtualized list
const POPOVER_HEADER_HEIGHT = 98;
const VIEWPORT_MARGIN = 16;

const UsersDropdownList: React.FC<Props> = ({
  userId,
  controlHeight,
  onUserChange,
  debounceChange = false,
  mtValue,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [listHeight, setListHeight] = useState(LIST_MAX_HEIGHT);
  const [openUpward, setOpenUpward] = useState(false);
  const { executeApi } = useApi();
  const theme = useTheme();
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const fetchUsers = async () => {
    const activeUsers = await executeApi<string, UserDto[]>(getActiveUsers, "");
    if (activeUsers) {
      const userList: User[] = activeUsers.map((user) => ({
        userId: user.userId,
        userName: user.userName,
        isChecked: user.userId === userId,
      }));
      setUsers(userList);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);
  
  const filteredUsers = useMemo(() => {
    return users.filter((user) =>
      user.userName?.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [users, searchText]);
  
  const isAllSelected = users.length > 0 && users.every((u) => u.isChecked);


  const isFilteredIndeterminate =
    filteredUsers.some((u) => u.isChecked) &&
    !filteredUsers.every((u) => u.isChecked);

  //* Handle global Select All
  const handleSelectAllClick = (checked: boolean) => {
    const updated = users.map((u) => ({ ...u, isChecked: checked }));
    setUsers(updated);
    onUserChange(updated.filter((u) => u.isChecked).map((u) => u.userId));
  };

  //* Debounced or direct onChange
  const debouncedChange = useMemo<
    DebouncedFunc<(ids: number[]) => void> | ((ids: number[]) => void)
  >(() => {
    return debounceChange
      ? debounce(onUserChange, ACTIVITIES.DEBOUNCE_TIME)
      : onUserChange;
  }, [onUserChange, debounceChange]);
   
  const handleItemToggle = (id: number) => {
    const updatedUsers = users.map((user) =>
      user.userId === id ? { ...user, isChecked: !user.isChecked } : user
    );

    setUsers(updatedUsers);
    const selectedIds = updatedUsers
      .filter((u) => u.isChecked)
      .map((u) => u.userId);

    debouncedChange(selectedIds);
  };

  // Cleanup for debounce without `any`
  useEffect(() => {
    if (
      debounceChange &&
      typeof (debouncedChange as DebouncedFunc<(ids: number[]) => void>).cancel === "function"
    ) {
      return () =>
        (debouncedChange as DebouncedFunc<(ids: number[]) => void>).cancel();
    }
  }, [debouncedChange, debounceChange]);

  //* Popover
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  //* Keep the dropdown inside the viewport instead of letting MUI shift it over the button
  const measureAvailableSpace = useCallback(() => {
    const anchor = buttonRef.current;
    if (!anchor) return;

    const { top, bottom } = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - bottom - VIEWPORT_MARGIN;
    const spaceAbove = top - VIEWPORT_MARGIN;
    const upward = spaceBelow < POPOVER_HEADER_HEIGHT + LIST_MIN_HEIGHT && spaceAbove > spaceBelow;
    const available = (upward ? spaceAbove : spaceBelow) - POPOVER_HEADER_HEIGHT;

    setOpenUpward(upward);
    setListHeight(Math.min(LIST_MAX_HEIGHT, Math.max(LIST_MIN_HEIGHT, available)));
  }, []);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", measureAvailableSpace);
    return () => window.removeEventListener("resize", measureAvailableSpace);
  }, [open, measureAvailableSpace]);

  //* Selected Display Text
  const selectedUsers = users.filter((u) => u.isChecked);

  const selectedText =
    selectedUsers.length === 0
      ? DROPDOWN_CONFIG.LABELS.EMPTY
      : isAllSelected
      ? DROPDOWN_CONFIG.LABELS.ALL
      : selectedUsers.length <= DROPDOWN_CONFIG.DISPLAY_LIMIT
      ? selectedUsers.map((u) => u.userName).join(", ")
      : `${selectedUsers.length} Users Selected`;

  //* Row Renderer (Virtualized)
  const Row = useCallback(
    ({ index, style, data }: ListChildComponentProps<User[]>) => {
      const user = data[index];
      return (
        <ListItem style={style} disablePadding key={user.userId}>
          <ListItemButton
            onClick={() => handleItemToggle(user.userId)}
            sx={{ py: 0, px: 1 }}
          >
            <Checkbox size="small" checked={user.isChecked} />
            <ListItemText
              primary={
                <Typography fontSize="0.85rem">{user.userName}</Typography>
              }
            />
          </ListItemButton>
        </ListItem>
      );
    },
    [handleItemToggle]
  );

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <label  htmlFor="usersSelect" className="floating-label" style={open ? { color: theme.palette.warning.main } : {}}>
        Users
      </label>

      {/* BUTTON */}
      <Button
        ref={buttonRef}
        onClick={() => {
          setSearchText("");
          measureAvailableSpace();
          setAnchorEl(buttonRef.current);
        }}
        endIcon={<ArrowDropDownIcon />}
        variant="outlined"
        size="small"
        fullWidth
        sx={{
          height: `${controlHeight}px`,
          justifyContent: "space-between",
          textTransform: "none",
          color: "text.secondary",
          border: `1px solid ${
            open ? theme.palette.warning.main : "rgba(161, 159, 159, 0.6)"
          }`,
          borderRadius: 2,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flexGrow: 1,
            textAlign: "left",
          }}
          title={selectedText}
        >
          {selectedText}
        </span>
      </Button>

      {/* DROPDOWN */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        TransitionComponent={Grow}
        anchorOrigin={{ vertical: openUpward ? "top" : "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: openUpward ? "bottom" : "top", horizontal: "left" }}
        slotProps={{
          paper: {
            style: {
              minWidth: anchorEl?.clientWidth || 200,
            },
            sx: mtValue ? (openUpward ? { mb: mtValue } : { mt: mtValue }) : undefined,
          },
        }}
      >
        {/* Search */}
        <Box px={1} pt={1} pb={0.5}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            fullWidth
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchText("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Select All */}
        <ListItem disablePadding>
          <ListItemButton sx={{ py: 0, px: 1 }} onClick={() => handleSelectAllClick(!isAllSelected)}>
            <Checkbox size="small" checked={isAllSelected} indeterminate={isFilteredIndeterminate} />
            <ListItemText
              primary={
                <Typography fontWeight="bold" fontSize="0.875rem">
                  Select All
                </Typography>
              }
            />
          </ListItemButton>
        </ListItem>

        {/* User List */}
        <FixedSizeList
          height={listHeight}
          itemCount={filteredUsers.length}
          itemSize={40}
          width="100%"
          itemData={filteredUsers}
        >
          {Row}
        </FixedSizeList>
      </Popover>
    </Box>
  );
};

export default UsersDropdownList;
